/**
 * Drawer — side-anchored modal panel built on Radix Dialog.
 *
 * The {@link Dialog} primitive renders a centered modal; the Drawer
 * renders the same focus-trapped surface anchored to one side of the
 * viewport so it can host larger payloads — explainers, evidence
 * stacks, registry browsers — without overflowing on tall screens.
 *
 * Reuses Radix Dialog's accessibility plumbing entirely: ARIA wiring,
 * focus management, scroll lock, and Escape-to-close all come from
 * the same primitive. Only the surface chrome changes.
 *
 * Usage:
 *
 *     <Drawer>
 *       <DrawerTrigger asChild><button type="button">Open</button></DrawerTrigger>
 *       <DrawerContent side="right">
 *         <DrawerTitle>Witness details</DrawerTitle>
 *         <DrawerDescription>...</DrawerDescription>
 *       </DrawerContent>
 *     </Drawer>
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "../../styling/index.js";

export type DrawerSide = "right" | "left" | "top" | "bottom";

export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;

export type DrawerOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;
export type DrawerTitleProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>;
export type DrawerDescriptionProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Description
>;
export interface DrawerContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /** Which edge the drawer is anchored to. Defaults to `right`. */
  readonly side?: DrawerSide;
  /**
   * Drawer width when anchored to `left` / `right`, or height when
   * anchored to `top` / `bottom`. Defaults to a responsive CSS clamp.
   */
  readonly size?: string;
}

export const DrawerOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  DrawerOverlayProps
>(function DrawerOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-40 bg-neutral-900/50 backdrop-blur-sm",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
        className,
      )}
      {...props}
    />
  );
});

const SIDE_CLASSES: Readonly<Record<DrawerSide, string>> = {
  right:
    "right-0 top-0 h-full data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right",
  left: "left-0 top-0 h-full data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left",
  top: "left-0 right-0 top-0 w-full data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top",
  bottom:
    "left-0 right-0 bottom-0 w-full data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
};

function defaultSize(side: DrawerSide): string {
  if (side === "left" || side === "right") return "min(26rem, calc(100vw - 2rem))";
  return "min(24rem, calc(100vh - 2rem))";
}

function sideSizeStyle(side: DrawerSide, size: string): React.CSSProperties {
  if (side === "left" || side === "right") return { width: size };
  return { height: size };
}

export const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(function DrawerContent({ className, children, side = "right", size, ...props }, ref) {
  const resolvedSize = size ?? defaultSize(side);
  return (
    <DialogPrimitive.Portal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        ref={ref}
        data-csk-drawer-side={side}
        className={cn(
          "fixed z-50 flex flex-col gap-3 overflow-y-auto border border-neutral-300 bg-white p-5",
          "text-sm leading-snug text-neutral-900 shadow-2xl outline-none",
          "data-[state=open]:animate-in data-[state=open]:duration-200",
          "data-[state=closed]:animate-out data-[state=closed]:duration-150",
          SIDE_CLASSES[side],
          className,
        )}
        style={sideSizeStyle(side, resolvedSize)}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
});

export const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  DrawerTitleProps
>(function DrawerTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-base font-semibold text-neutral-900", className)}
      {...props}
    />
  );
});

export const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  DrawerDescriptionProps
>(function DrawerDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm text-neutral-700", className)}
      {...props}
    />
  );
});
